import { sendToApi } from 'api/client'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { buildErrorMessage } from 'utils'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { BatchCampaignPreparation, BrandPlanScheduleCrosspost, Source } from 'types'
import { UseMutationOptions } from 'react-query/types/react/types'
import { useMutation } from 'react-query'


export type PreparationCampaign = Partial<Omit<BrandPlanScheduleCrosspost, 'media_files'>> & { topic: string; sources?: Array<Source>; media_files?: Array<{ id: number }> }

export interface Variables {
  campaigns: Array<PreparationCampaign>
  start_date?: string
  signal?: AbortSignal
}

export interface Props {
  brandPlanId: number
  workspaceId: number
}

export interface Data {
  batch_campaign_preparation: BatchCampaignPreparation
}

export type Options = Omit<UseMutationOptions<Data, Error, Variables>, 'mutationFn'>

export const createBatchCampaignPreparation = async ({
  workspaceId,
  brandPlanId,
  start_date,
  campaigns,
  signal,
}: Variables & Props) => {
  const res = await sendToApi<Data>(`/w/${workspaceId}/brand_plans/${brandPlanId}/batch_campaign_preparations`, {
    method: 'POST',
    body: {
      start_date,
      campaigns,
    },
    signal,
  })

  if (res.type === 'exception' && res.data instanceof DOMException && res.data.name === 'AbortError') {
    throw res
  }

  if (res.type !== SUCCESS_RESPONSE_TYPE) {
    if (res.data === 'cancel') {
      throw new DOMException('The operation was aborted.', 'AbortError')
    }
    throw new Error(
      buildErrorMessage(res.data.errors) || 'An error occurred! Our team has been notified and we are looking into it.',
    )
  }

  return res.data
}

export const useCreateBatchCampaignPreparation = ({ brandPlanId }: Omit<Props, 'workspaceId'>, options?: Options) => {
  const workspaceId = useCurrentWorkspaceId()
  return useMutation<Data, Error, Variables>(
    (vars) => createBatchCampaignPreparation({ brandPlanId, workspaceId, ...vars }),
    options,
  )
}
